// 청구서 납부 과정 > 1. 청구서 내용 확인

import { rootStore } from "@/mobx/store";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

import Button from "@/components/button";
import IconCheck from "@/components/icons/check";
import { ChevronRight } from "@/components/icons/chevron";
import KakaoPayIcon from "@/components/icons/kakao_pay";
import NaverPayIcon from "@/components/icons/naver_pay";
import ResetIcon from "@/components/icons/reset";
import IconX from "@/components/icons/x";
import { Page_settings } from "@/config/page_settings";
import colors from "@/design/colors";
import fonts from "@/design/typography";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";

import AlertModal from "@/components/common/AlertModal";

import { API_URL_CHEWING } from "@/config/const";

import CariPayLogoBlack from "@/components/icons/cariPayLogoBlack";
import {
  EpayCard,
  EpayCardPanel,
  defaultEpayCardId,
  EPAY_CARD_REGISTERED_MESSAGE,
  LinkPayCardNotice,
  EpayExpiredLinkNotice,
  EpayPhoneCheck,
  isEpayLinkExpired,
  needsEpayPhoneCheck,
  isSuccessfulEpayPaymentReturn,
  parseEpayReturn,
} from "./EpayCardPanel";
import { paymentLink } from "../paymentLink";
import { preselectFromSearch } from "./preselect";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "EPAY" | "CARD" | "NAVER" | "KAKAO" | null;

/* **********************************
 * CONSTANTS
 * 상수 정의
 ********************************** */
const PAYMENT_METHODS = [
  // 저장 카드(NICE ePAY) 결제 방법. 고객에게는 "카드등록"으로 보인다(대표 결정 10-20).
  { id: "EPAY", label: "카드등록" },
  { id: "CARD", label: "앱 카드" },
  { id: "NAVER", label: <NaverPayIcon /> },
  { id: "KAKAO", label: <KakaoPayIcon /> },
] as const;

interface EpayFormResponse {
  action: string;
  fields: Record<string, string>;
  flowId: number;
}

interface PaymentLineItem {
  name: string;
  unitPrice: number;
  discountAmount: number | null;
  publisher?: string;
  imageUrl?: string;
  qty?: number;
}

type OrderType = "BILL" | "SHOP";

// 통신판매중개자 면책 문구 (츄잉샵 §1-5 / 추가 논의사항)
const SHOP_DISCLAIMER_TEXT =
  "본 결제 요청에 대한 상품의 배송, 교환 및 환불에 대한 의무와 책임은 제휴사 및 해당 학원(기관)에 있습니다. 카리페이는 통신판매중개자(결제 시스템 제공자)로서 통신판매의 당사자가 아니며, 상품 결함 및 배송에 대한 법적 책임을 지지 않습니다.";

// 샵 배송 안내 문구 (츄잉샵 §1-2)
const SHOP_SHIPPING_NOTICE = "결제 완료된 상품은 학원으로 배송됩니다.";

// 카리페이(ePAY) 오프라인 승인은 이 금액을 넘으면 고객 서명이 필요하다(서버 NICE_EPAY_SIGNATURE_THRESHOLD 와 같은 값).
const EPAY_SIGNATURE_THRESHOLD = 50000;

const BillConfirmPage = () => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const navigate = useNavigate();
  const context: any = useContext(Page_settings);

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  // 결제 방법 state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);

  // 카드사, 할부 개월 선택 state
  const [selectedCard, setSelectedCard] = useState("");
  const [epayEnabled, setEpayEnabled] = useState(false);
  // 토큰 없는(또는 만료된) 링크에서 '휴대폰 뒷자리 확인 → 저장 카드 결제' 를 보여줄지(서버 판단).
  const [epayPhoneCheck, setEpayPhoneCheck] = useState(false);
  const [epayPhoneCheckBusy, setEpayPhoneCheckBusy] = useState(false);
  const [epayPhoneCheckError, setEpayPhoneCheckError] = useState("");
  const [epayPaymentEnabled, setEpayPaymentEnabled] = useState(false);
  const [epayAppCardRegistrationEnabled, setEpayAppCardRegistrationEnabled] = useState(false);
  const [epayAuthOnlyTest, setEpayAuthOnlyTest] = useState(false);
  const [epayCards, setEpayCards] = useState<EpayCard[]>([]);
  const [selectedEpayCardId, setSelectedEpayCardId] = useState<number | null>(null);
  const [epayBusy, setEpayBusy] = useState(false);
  const [installmentMonth, setInstallmentMonth] = useState("0");
  const [isInstallmentMonthDisabled, setIsInstallmentMonthDisabled] = useState(true);

  // 약관동의 state
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeChewing, setAgreeChewing] = useState(false);
  const [agreePartners, setAgreePartners] = useState(false);

  // 서명 state
  const [showSignatureSheet, setShowSignatureSheet] = useState(false);
  const [signatureData, setSignatureData] = useState<null | {
    data: string;
    width: number;
    height: number;
  }>(null);
  const signatureRef = useRef<SignatureCanvas>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(558);
  const canvasHeight = 180;

  // 약관 동의 state
  const [agreementType, setAgreementType] = useState<
    "third-party" | "privacy" | null
  >(null);
  // 약관 내용 state
  const [content, setContent] = useState<string>("");

  // 약관동의 바텀시트 state
  const [showAgreementSheet, setShowAgreementSheet] = useState(false);

  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);

  const [approvalAmountNum, setApprovalAmountNum] = useState(0);
  const [approvalAmount, setApprovalAmount] = useState("0");
  const [storeName, setStoreName] = useState("");
  const [payUserName, setPayUserName] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [buttonString, setButtonString] = useState("");

  const [showAlert, setShowAlert] = useState(false); // 선택 안 했을 때
  const [confirmType, setConfirmType] = useState<"delete" | "resend" | null>(
    null
  );
  const [alertMessage, setAlertMessage] = useState("");
  const [lineItemList, setLineItemList] = useState<PaymentLineItem[]>([]);
  const [orderType, setOrderType] = useState<OrderType>("BILL");
  

  // //삭제
  // const handleDelete = () => {
  //   if (selectedBills.length === 0) return setShowAlert(true);
  //   setConfirmType("delete");
  // };

  // // 재발송
  // const handleResend = () => {
  //   if (selectedBills.length === 0) return setShowAlert(true);
  //   setConfirmType("resend");
  // };

  
  // 버튼 활성화 조건
  const isButtonActive =
    !!paymentMethod &&
    ((paymentMethod === "EPAY" && epayPaymentEnabled && agreeAll && !!selectedEpayCardId) ||
      (paymentMethod === "CARD" &&
      agreeAll &&
      !!selectedCard ) ||
      ((paymentMethod === "NAVER" || paymentMethod === "KAKAO") && agreeAll));

  // ref
  const agreementBottomSheetRef = useRef<HTMLDivElement>(null); // 약관동의 바텀시트 ref
  const signatureBottomSheetRef = useRef<HTMLDivElement>(null); // 서명 바텀시트 ref

  /* **********************************
   * HOOKS
   ********************************** */
  useClickOutsideCloseModal([signatureBottomSheetRef], () => {
    // 닫힐 때 현재 서명이 비었으면 signatureData도 제거
    if (!signatureData) {
      setIsSignatureEmpty(true);
    }

    setShowSignatureSheet(false);
  });

  useClickOutsideCloseModal([agreementBottomSheetRef], () => {
    setShowAgreementSheet(false);
  });

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  // 캔버스 width 동적 측정
  useEffect(() => {
    if (
      !showSignatureSheet ||
      !signatureRef.current ||
      !canvasContainerRef.current
    )
      return;

    const container = canvasContainerRef.current;
    const canvas = signatureRef.current.getCanvas();
    const ratio = window.devicePixelRatio || 1;

    // 컨테이너의 실제 크기를 기준으로 캔버스 크기 설정
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // 실제 픽셀 크기 설정
    canvas.width = containerWidth * ratio;
    canvas.height = containerHeight * ratio;

    // CSS 상에서 보이는 크기 유지
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }

    // 기존 서명 복원 (옵션)
    if (signatureData?.data) {
      setTimeout(() => {
        signatureRef.current?.fromDataURL(signatureData.data);
      }, 0);
    }
  }, [showSignatureSheet, signatureData]);

  //약관 동의 내용 가져오기
  useEffect(() => {
    if (!agreementType) return;

    context.get(API_URL_CHEWING + "/app/v1/setting/terms/"+ agreementType, {}, (res: any) => {
      setContent(res);
    });
  }, [agreementType]);

   // 약관 동의 내용 가져오기
  // useEffect(() => {
  //   if (!agreementType) return;
  //   alert(API_URL_CHEWING + "/app/v1/setting/terms/"+ agreementType);

  //   context.get(API_URL_CHEWING + "/app/v1/setting/terms/"+ agreementType, {
  //       // headers: {
  //       //   "Content-Type": "application/json",
  //       // },
  //     })
  //     .then((res: any) => {
  //       // axios 응답에서 data 속성을 추출
  //       setContent(res.data || "");
  //     })
  //     .catch((error) => {
  //       console.error("약관 내용을 가져오는데 실패했습니다:", error);
  //       setContent("");
  //     });
  // }, [agreementType]);

  // 결제 정보 저장
  useEffect(() => {
    setPaymentMethod(paymentMethod);
    setSelectedCard(selectedCard);
    setInstallmentMonth(installmentMonth);
    setSignatureData(signatureData);
  }, [paymentMethod, selectedCard, installmentMonth, signatureData]);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */
  // 모두 동의 핸들러
  const handleToggleAll = () => {
    const newValue = !agreeAll;
    setAgreeAll(newValue);
    setAgreeChewing(newValue);
    setAgreePartners(newValue);
  };

  // 개별 동의 핸들러
  const handleToggleChewing = () => {
    const newValue = !agreeChewing;
    setAgreeChewing(newValue);
    setAgreeAll(newValue && agreePartners);
  };

  const handleTogglePartners = () => {
    const newValue = !agreePartners;
    setAgreePartners(newValue);
    setAgreeAll(newValue && agreeChewing);
  };

  
  // 처음 진입 시 결제 정보 초기화
  useEffect(() => {
    rootStore.clearPaymentInfo();
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");

    if (txId && isSuccessfulEpayPaymentReturn(window.location.search)) {
      rootStore.setPaymentInfo({ txId, accessToken: accessToken || "" });
      navigate("/payments/pending");
      return;
    }

    getPaymentInfo();
    if (txId && accessToken) {
      loadEpayMethods(txId);
    }

    const { step: epayStep, code: epayCode, message } = parseEpayReturn(window.location.search);
    const preselected = epayStep ? null : preselectFromSearch(window.location.search);
    if (preselected) {
      setPaymentMethod(preselected.method);
      if (preselected.method === "CARD") setSelectedCard(preselected.card);
    }
    if (epayStep === "card" && epayCode === "0000") {
      setPaymentMethod("EPAY");
      setAlertMessage(EPAY_CARD_REGISTERED_MESSAGE);
      setShowAlert(true);
    } else if (epayStep === "pin" && epayCode === "0000") {
      setPaymentMethod("EPAY");
      setAlertMessage("결제비밀번호를 변경했어요.");
      setShowAlert(true);
    } else if (epayStep === "terms" && epayCode === "0000") {
      setPaymentMethod("EPAY");
      setAlertMessage("카리페이 약관 확인을 마쳤어요.");
      setShowAlert(true);
    } else if (epayStep === "payment" && epayCode === "AUTH_ONLY") {
      // 개발 서버: NICE 결제 인증까지만 하고 승인은 하지 않았다(서버가 AUTH_ONLY 로 돌려준다).
      setPaymentMethod("EPAY");
      setAlertMessage(message || "결제 인증까지 확인했어요. 개발 서버라 승인(출금)은 하지 않았어요.");
      setShowAlert(true);
    } else if (epayCode && epayCode !== "0000") {
      const fallback = epayStep === "pin"
        ? "결제비밀번호를 변경하지 못했어요. 다시 시도해주세요."
        : epayStep === "terms"
          ? "약관 화면을 완료하지 못했어요. 다시 시도해주세요."
          : "결제를 완료하지 못했어요. 다시 시도해주세요.";
      setAlertMessage(message || fallback);
      setShowAlert(true);
    }
  }, []);

  const [customerSign, setCustomerSign] = useState("");
 // const [paymentCompCode, setPaymentCompCode] = useState("");
  
  //결제하기
  const onPayment = () => {
    const { txId, accessToken } = paymentLink();
    let paymentCompCode = "";
    let appType = "A";
    let signData = customerSign;

    if (paymentMethod === "EPAY") {
      if (!epayPaymentEnabled) {
        setAlertMessage("NICE 승인·서명 모듈 연결이 완료된 뒤 결제할 수 있어요.");
        setShowAlert(true);
        return;
      }
      if (!txId || !selectedEpayCardId) {
        setAlertMessage("결제할 카드를 선택해주세요.");
        setShowAlert(true);
        return;
      }
      // 5만원 초과는 서명 필수(NICE 오프라인 승인 규격). 서버가 서명 이미지를 NICE 서명 데이터로 바꾼다.
      const needsSignature = approvalAmountNum > EPAY_SIGNATURE_THRESHOLD;
      if (needsSignature && !signatureData?.data) {
        setShowSignatureSheet(true);
        return;
      }
      startEpayPayment(txId, selectedEpayCardId, needsSignature ? signatureData?.data ?? null : null);
      return;
    }

    if(approvalAmountNum >= 50000){
      // 서명이 이미 되어 있으면 바로 다음 페이지로
      if (signData) {
        setShowSignatureSheet(false);
      } else {
        setShowSignatureSheet(true);
        return;
      }
    }else{
      //setCustomerSign("");
    }

    // 결제 정보 저장
    rootStore.setPaymentInfo({
      method: paymentMethod,
      cardCompany: selectedCard,
      installment: installmentMonth,
      signature: signData,
    });

    if(paymentMethod === "CARD"){
      paymentCompCode = selectedCard;      
    }else if(paymentMethod === "NAVER"){
      paymentCompCode = "NAVER"; 
    }else if(paymentMethod === "KAKAO"){
      paymentCompCode = "KKP2"; 
    }

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      appType = "A";
    }else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appType = "I";
    }else{
      appType = "A";
    }

    // console.log({
    //     txId: txId,
    //     installmentMonth: installmentMonth,
    //     paymentCompCode: paymentCompCode,
    //     customerSign: signData,
    //     appType: appType,
    //   });
    
    context.post(
      `/payment/requestPaymentLink`,
      {
        txId: txId,
        accessToken: accessToken,
        installmentMonth: installmentMonth,
        paymentCompCode: paymentCompCode,
        customerSign: signData,
        appType: appType,
      },
      (response) => {
        console.log(response);

        if (!response) {
          alert("결제 처리 중 오류가 발생했습니다.");
          return;
        }

        if(response.RESULT_CODE === "0000"){
          goWebCard(response);
        }else{
          if(response.processStatus === "APPROVE_COMPLETE" || response.processStatus === "CANCEL_COMPLETE"){
            alert("이미 처리 된 결제요청입니다.");
            navigate("/payments/result");
          }else if(response.processStatus === "STORE_DELETE"){
            alert("삭제 된 결제요청입니다.");
            //setAlertMessage("요청삭제 된 결제입니다. /n가맹점에 문의해 주세요.");
            //setShowAlert(true);
            navigate("/payments/result");
          }else{
            alert(response.RESULT_MSG);
          }
          
        }
        //setListSearchStatus((prev) => !prev);
      }
    );
  };

  const loadEpayMethods = (txId: string) => {
    const accessToken = new URLSearchParams(window.location.search).get("accessToken");
    if (!accessToken) {
      setEpayEnabled(false);
      setEpayPaymentEnabled(false);
      return;
    }
    context.post(
      "/epay/methods",
      { txId, accessToken },
      (response: { enabled: boolean; paymentEnabled: boolean; appCardRegistrationEnabled?: boolean; authOnlyTest?: boolean; methods: EpayCard[] }) => {
        setEpayEnabled(!!response?.enabled);
        setEpayPaymentEnabled(!!response?.paymentEnabled);
        setEpayAppCardRegistrationEnabled(!!response?.appCardRegistrationEnabled);
        setEpayAuthOnlyTest(!!response?.authOnlyTest);
        const methods = response?.methods || [];
        setEpayCards(methods);
        setSelectedEpayCardId(defaultEpayCardId(methods, !!response?.paymentEnabled));
      },
      () => {
        setEpayEnabled(false);
        setEpayPaymentEnabled(false);
        setEpayCards([]);
        setEpayAppCardRegistrationEnabled(false);
        setEpayAuthOnlyTest(false);
        setSelectedEpayCardId(null);
      },
      false
    );
  };

  const submitEpayForm = (response: EpayFormResponse) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = response.action;
    form.acceptCharset = "UTF-8";
    Object.entries(response.fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value ?? "";
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const startEpayPayment = (
    txId: string,
    paymentMethodId: number,
    signImage: string | null
  ) => {
    if (epayBusy) return;
    const accessToken = new URLSearchParams(window.location.search).get("accessToken");
    if (!accessToken) {
      setAlertMessage("유효한 카드등록 결제 링크가 필요해요.");
      setShowAlert(true);
      return;
    }
    setEpayBusy(true);
    context.post(
      "/epay/payment/start",
      { txId, accessToken, paymentMethodId, installmentMonth, signImage },
      (response: EpayFormResponse) => {
        setEpayBusy(false);
        submitEpayForm(response);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || "카드등록 결제를 시작하지 못했어요.");
        setShowAlert(true);
      }
    );
  };

  const registerEpayCard = () => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");
    if (!txId || !accessToken || epayBusy) return;
    setEpayBusy(true);
    context.post(
      "/epay/card-registration/start",
      { txId, accessToken },
      (response: EpayFormResponse) => {
        setEpayBusy(false);
        submitEpayForm(response);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || "카드 등록을 시작하지 못했어요.");
        setShowAlert(true);
      }
    );
  };

  const openEpayManagementPage = (path: string, fallbackMessage: string) => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");
    if (!txId || !accessToken || epayBusy) return;
    setEpayBusy(true);
    context.post(
      path,
      { txId, accessToken },
      (response: EpayFormResponse) => {
        setEpayBusy(false);
        submitEpayForm(response);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || fallbackMessage);
        setShowAlert(true);
      }
    );
  };

  const changeEpayPin = () => openEpayManagementPage(
    "/epay/pin-change/start",
    "결제비밀번호 변경 화면을 열지 못했어요."
  );

  const viewEpayTerms = () => openEpayManagementPage(
    "/epay/terms/start",
    "카리페이 약관 화면을 열지 못했어요."
  );

  // 받는 분 휴대폰 뒷자리가 맞으면 서버가 새 결제창 주소(접근 토큰 30분)를 준다 — 그 주소로 다시 연다.
  const submitEpayPhoneCheck = (phoneLast4: string) => {
    const { txId, accessToken } = paymentLink();
    if (!txId || epayPhoneCheckBusy) return;
    setEpayPhoneCheckBusy(true);
    setEpayPhoneCheckError("");
    context.post(
      "/payment/epayLink",
      { txId, accessToken, phoneLast4 },
      (response: { redirectUrl?: string }) => {
        if (response?.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }
        setEpayPhoneCheckBusy(false);
        setEpayPhoneCheckError("카드등록 결제를 열지 못했어요. 잠시 후 다시 시도해 주세요.");
      },
      (message: string) => {
        setEpayPhoneCheckBusy(false);
        setEpayPhoneCheckError(message || "카드등록 결제를 열지 못했어요. 잠시 후 다시 시도해 주세요.");
      },
      false
    );
  };

  const makeDefaultEpayCard = (paymentMethodId: number) => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");
    if (!txId || !accessToken || epayBusy) return;
    setEpayBusy(true);
    context.post(
      "/epay/methods/default",
      { txId, accessToken, paymentMethodId },
      () => {
        setEpayBusy(false);
        loadEpayMethods(txId);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || "기본 카드를 바꾸지 못했어요.");
        setShowAlert(true);
      },
      false
    );
  };

  const deleteEpayCard = (paymentMethodId: number) => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");
    if (!txId || !accessToken || epayBusy || !window.confirm("이 카드를 카리페이에서 삭제할까요?")) return;
    setEpayBusy(true);
    context.post(
      "/epay/methods/delete",
      { txId, accessToken, paymentMethodId },
      () => {
        setEpayBusy(false);
        loadEpayMethods(txId);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || "카드를 삭제하지 못했어요.");
        setShowAlert(true);
      }
    );
  };

  const withdrawEpayMember = () => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get("txId");
    const accessToken = params.get("accessToken");
    if (!txId || !accessToken || epayBusy) return;
    if (!window.confirm("간편결제를 해지하면 NICE ePAY에 등록된 카드가 모두 삭제됩니다. 해지할까요?")) return;
    setEpayBusy(true);
    context.post(
      "/epay/member/withdraw",
      { txId, accessToken },
      () => {
        setEpayBusy(false);
        setSelectedEpayCardId(null);
        loadEpayMethods(txId);
        setAlertMessage("카리페이 간편결제를 해지했어요.");
        setShowAlert(true);
      },
      (message: string) => {
        setEpayBusy(false);
        setAlertMessage(message || "간편결제를 해지하지 못했어요.");
        setShowAlert(true);
      }
    );
  };

  //결제서비스 앱카드 호출
  const goWebCard = (data) => {
    
    const paymentCompCode = data.PAYMENT_COMP_CODE;
		let appUrl = data.URL;

    //console.log("appUrl:" + appUrl);

    //카카오페이
		if(paymentCompCode === "KKP2"){
			appUrl = data.KAKAO_APP_SCHEME;
		} else if (paymentCompCode === "BC") {
      // data.URL 로 POST 요청
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.URL;
      form.target = '_self'; // 또는 '_blank'로 새창 띄우고 싶으면 '_blank'
      
      Object.keys(data).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
      });
  
      document.body.appendChild(form);
      form.submit();
      return; // ⛔ 이후 앱 호출 로직 중단
    } else {
			appUrl = data.URL;
		}

    rootStore.setPaymentInfo({
      appUrl: appUrl,
    });

    const now = Date.now();
    let appOpened = false;
    let appInstallUrl = "";

    window.location.href = appUrl;

    // 하나카드
    if(appUrl.indexOf("cloudpay") != -1)
    {
        appInstallUrl = "https://m.hanacard.co.kr/mobapp/slink.htm";
    }

    // 국민카드
    if(appUrl.indexOf("kbpay-remotepay") != -1)
    {
        appInstallUrl = "https://url.kr/zho1fd";
    }

    // 신한카드
    if(appUrl.indexOf("shinhan-sr-ansimclick") != -1)
    {
        if (navigator.userAgent.toLowerCase().indexOf("ipad") != -1 || navigator.userAgent.toLowerCase().indexOf("iphone") != -1 || navigator.userAgent.toLowerCase().indexOf("iphone") != -1)
        {
            appInstallUrl = "https://itunes.apple.com/kr/app/sinhan-mobilegyeolje/id572462317?mt=8";
        }
        else
        {
            appInstallUrl = "market://details?id=com.shcard.smartpay";
        }
    }

    // 현대카드
    if(appUrl.indexOf("hdcardappcardansimclick") != -1)
    {
        appInstallUrl = "https://ptx3p.app.goo.gl/31kZ";
    }

    // 우리카드
    if(appUrl.indexOf("wooricard") != -1)
    {
        appInstallUrl = "https://m.wooricard.com/dcmw/yh1/mlf/mlf03/useadv/M1MLF203S00.do";
    }

    // 삼성카드
    if(appUrl.indexOf("samsungcard") != -1)
    {
        appInstallUrl = appUrl;
    }

    // 카카오페이 kakaopay
    //if(strPayUrl.indexOf("kakaopay.com") != -1)
    if(appUrl.indexOf("kakaopay") != -1)
    {
        appInstallUrl = appUrl;
    }

    // 네이버페이
    if(appUrl.indexOf("pay.naver.com") != -1)
    {
        appInstallUrl = appUrl;
    }

    // 백그라운드 상태 감지
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 앱이 열리면 브라우저가 백그라운드로 가므로 이 시점에 이벤트 발생
        appOpened = true;
        //console.log("✅ 앱이 열렸다고 추정됩니다.");
        // 🔥 여기에 원하는 이벤트 추가
        navigate("/payments/pending");
      }
    });

    // 일정 시간 후 fallback (앱이 열리지 않았을 경우)
    setTimeout(() => {
      if (!appOpened) {
        //console.log("❌ 앱이 열리지 않았습니다. 앱스토어로 이동합니다.");
        window.location.href = appInstallUrl;
      }
    }, 1500);

    // 예: 보내고 싶은 데이터
    // const payload = {
    //   userId: 'hong123',
    //   amount: 10000,
    //   callbackUrl: 'https://yourdomain.com/return',
    // };

    // // 1. form 요소 생성
    // const form = document.createElement('webCardform');
    // //form.enctype = 'application/x-www-form-urlencoded';
    // form.method = 'POST';
    // form.action =  data.URL;
    // form.target = '_self'; // 또는 '_blank'로 새창도 가능

    // // 2. 숨겨진 input 추가
    // Object.keys(data).forEach((key) => {
    //   const input = document.createElement('input');
    //   input.type = 'hidden';
    //   input.name = key;
    //   input.value = data[key];
    //   form.appendChild(input);
    // });

    // // 3. form을 문서에 추가 후 submit
    // document.body.appendChild(form);
    // form.submit();
  };

  //결제서비스 앱카드 호출
  const getPaymentInfo = () => {
    //결제정보요청  
    const { txId, accessToken } = paymentLink();

    rootStore.setPaymentInfo({
      txId: txId,
      accessToken: accessToken,
    });
   
    context.post(
      `/payment/requestPaymentInfo`,
      {
        txId: txId,
        accessToken: accessToken,
      },
      (response) => {
        if(!response || !response.processStatus){
          alert("결제 정보를 불러올 수 없습니다.");
          return;
        }
        if(response.processStatus === "APPROVE_COMPLETE" || response.processStatus === "CANCEL_COMPLETE"){
          navigate("/payments/result");
        }else if(response.processStatus === "STORE_DELETE"){
          alert("삭제 된 결제요청입니다.");
          navigate("/payments/result");
        }else if(response.processStatus === "CANCEL_FAIL"){
          // 앞선 결제가 카드사·간편결제사에 승인으로 남아 있을 수 있다. 결제 화면을 보여주면 다른 수단으로
          // 다시 결제해 이중 출금이 난다(2026-10-12). 확인 안내 화면으로 바꿔 끼운다(뒤로 가기로 돌아오지 않게).
          navigate("/payments/pending", { replace: true });
        }else{
          setPaymentData(response);
        }
      }
    );
  };

    
  const setPaymentData = (data) => {
    //alert(data.approvalAmount);
    if(data.approvalAmount >= 50000){
      setIsInstallmentMonthDisabled(false);
    }else{
      setIsInstallmentMonthDisabled(true);
    }
    
    setApprovalAmountNum(data.approvalAmount);
    setApprovalAmount(data.approvalAmount.toLocaleString());
    setStoreName(data.storeName);
    setPayUserName(data.payUserName);
    setRequestReason(data.requestReason);
    setInfoMessage(data.infoMessage);
    setLineItemList(data.paymentLineItems?.items || []);
    // orderType 수신 (서버 미전달 시 BILL 기본)
    setOrderType(data.orderType === "SHOP" ? "SHOP" : "BILL");
    setEpayPhoneCheck(!!data.savedCardPhoneCheck);
    const buttonString = "총 " + data.approvalAmount.toLocaleString() + "원 결제하기";
    setButtonString(buttonString);
    
  };

  return (
    <Container>
      <Wrapper>
        {/* 헤더 */}
        <HeaderWrapper>
          <CariPayLogoBlack width="148" height="28" />
        </HeaderWrapper>

        {/* contents */}
        <ContentWrapper>
          {/* 청구 내역 */}
          <ContentBox>
            <Title>청구 내역</Title>

            <InfoGrid>
              <Row>
                <Label>청구서 발급처</Label>
                <Value>{storeName}</Value>
              </Row>
              <Row>
                <Label>수납인</Label>
                <Value>{payUserName}</Value>
              </Row>
              <Row>
                <Label>청구사유</Label>
                <Value>{requestReason}</Value>
              </Row>
              <Row>
                <Label>안내메세지</Label>
                <Value>{infoMessage}</Value>
              </Row>
            </InfoGrid>

            {/* 샵 분기: 수신인 정보 아래 배송 안내 캡션 (§1-2) */}
            {orderType === "SHOP" && (
              <ShippingNotice role="note">{SHOP_SHIPPING_NOTICE}</ShippingNotice>
            )}

            <Divider />

            <TotalRow>
              <TotalLabel>총 결제 금액</TotalLabel>
              <TotalValue>{approvalAmount}원</TotalValue>
            </TotalRow>

            {lineItemList.length > 0 && orderType === "SHOP" && (
              <ShopItemTable role="table" aria-label="상품 내역">
                <ShopItemRowHeader role="row">
                  <ShopColName role="columnheader">교재</ShopColName>
                  <ShopColCover role="columnheader">표지</ShopColCover>
                  <ShopColPublisher role="columnheader">출판사</ShopColPublisher>
                  <ShopColQty role="columnheader">
                    수량 (단가 × N = 소계)
                  </ShopColQty>
                </ShopItemRowHeader>
                {lineItemList.map((item, idx) => {
                  const qty = item.qty ?? 1;
                  const unit =
                    item.unitPrice - (item.discountAmount ?? 0);
                  const subtotal = unit * qty;
                  return (
                    <ShopItemRow
                      key={`${item.name}-${idx}`}
                      role="row"
                    >
                      <ShopColName role="cell">{item.name}</ShopColName>
                      <ShopColCover role="cell">
                        {item.imageUrl ? (
                          <ShopCoverImg
                            src={item.imageUrl}
                            alt={`${item.name} 표지`}
                            loading="lazy"
                          />
                        ) : (
                          <ShopCoverPlaceholder
                            aria-label="표지 이미지 없음"
                            role="img"
                          >
                            <span aria-hidden="true">📕</span>
                          </ShopCoverPlaceholder>
                        )}
                      </ShopColCover>
                      <ShopColPublisher role="cell">
                        {item.publisher ?? "-"}
                      </ShopColPublisher>
                      <ShopColQty role="cell">
                        {`${qty}개 (${unit.toLocaleString()}원 × ${qty} = ${subtotal.toLocaleString()}원)`}
                      </ShopColQty>
                    </ShopItemRow>
                  );
                })}
              </ShopItemTable>
            )}

            {/* BILL (기존) 렌더링 유지 - 회귀 방지 */}
            {lineItemList.length > 0 && orderType !== "SHOP" && (
              <LineItemList>
                {lineItemList.map((item) => (
                  <LineItem key={item.name}>{item.name} {(item.unitPrice - (item.discountAmount ?? 0)).toLocaleString()}원</LineItem>
                ))}
              </LineItemList>
            )}
          </ContentBox>

          {/* 결제 방법 */}
          <ContentBox>
            <Title>결제 방법</Title>
            {epayPhoneCheck && !epayEnabled && needsEpayPhoneCheck(window.location.search) ? (
              <EpayPhoneCheck busy={epayPhoneCheckBusy} error={epayPhoneCheckError} onSubmit={submitEpayPhoneCheck} />
            ) : (
              isEpayLinkExpired(window.location.search) && <EpayExpiredLinkNotice />
            )}
            <PaymentMethodGrid>
              {PAYMENT_METHODS.filter((method) => method.id !== "EPAY" || epayEnabled).map((method) => (
                <PaymentMethodItem
                  key={method.id}
                  $isActive={paymentMethod === method.id}
                  onClick={() => {
                    if (paymentMethod === method.id) return;

                    setPaymentMethod(method.id); // 결제 방법 변경
                    setSelectedCard(""); // 카드사 초기화
                    setSelectedEpayCardId(method.id === "EPAY" ? defaultEpayCardId(epayCards, epayPaymentEnabled) : null);
                    setInstallmentMonth("0"); // 할부 개월 초기화
                    setAgreeAll(false); // 전체 동의 초기화
                    setAgreeChewing(false); // 개별 동의 초기화
                    setAgreePartners(false); // 개별 동의 초기화
                    setSignatureData(null); // 서명 데이터 초기화
                    setIsSignatureEmpty(true); // 서명 데이터 비어있는지 확인 (버튼 disalbed 처리)
                  }}
                >
                  <PaymentMethodItemTitle>
                    {method.label}
                  </PaymentMethodItemTitle>
                </PaymentMethodItem>
              ))}
            </PaymentMethodGrid>

            {paymentMethod === "EPAY" && (
              <EpayCardPanel
                cards={epayCards}
                selectedCardId={selectedEpayCardId}
                paymentEnabled={epayPaymentEnabled}
                appCardRegistrationEnabled={epayAppCardRegistrationEnabled}
                authOnlyTest={epayAuthOnlyTest}
                busy={epayBusy}
                onSelect={setSelectedEpayCardId}
                onRegister={registerEpayCard}
                onMakeDefault={makeDefaultEpayCard}
                onDelete={deleteEpayCard}
                onChangePin={changeEpayPin}
                onViewTerms={viewEpayTerms}
                onWithdraw={withdrawEpayMember}
              />
            )}

            {/* 앱카드 선택 시에만 보여줌 */}
            {(paymentMethod === "CARD") && (
              <>
                <SelectWrapper>
                    <NativeSelect
                      value={selectedCard}
                      onChange={(e) => setSelectedCard(e.target.value)}
                      $selected={!!selectedCard}
                    >
                      <option value="">카드사 선택</option>
                      <option value="KBPAY">국민카드</option>
                      <option value="SHINHAN">신한카드</option>
                      <option value="HANA">하나카드</option>
                      <option value="HYUNDAI">현대카드</option>
                      <option value="WOORI">우리카드</option>
                      <option value="SAMSUNG">삼성카드</option>
                      <option value="BC">BC카드</option>
                    </NativeSelect>
                  </SelectWrapper>
                <LinkPayCardNotice />
              </>
            )}
            {(paymentMethod === "EPAY" || paymentMethod === "CARD" || paymentMethod === "NAVER" || paymentMethod === "KAKAO") && (
              <>
                <SelectWrapper>
                  <NativeSelect
                        value={installmentMonth}
                        disabled={isInstallmentMonthDisabled} 
                        onChange={(e) => setInstallmentMonth(e.target.value)}
                        $selected={!!installmentMonth}
                      >
                        <option value="0">일시불</option>
                        <option value="2">2개월</option>
                        <option value="3">3개월</option>
                        <option value="4">4개월</option>
                        <option value="5">5개월</option>
                        <option value="6">6개월</option>
                        <option value="7">7개월</option>
                        <option value="8">8개월</option>
                        <option value="9">9개월</option>
                        <option value="10">10개월</option>
                        <option value="11">11개월</option>
                        <option value="12">12개월</option>
                    </NativeSelect>       
                  </SelectWrapper>         
              </>
            )}
          </ContentBox>

          {/* 약관 동의 */}
          <ContentBox style={{ padding: "24px 16px" }}>
            <AgreementToggle style={{ marginBottom: 16 }}>
              <CheckboxInput checked={agreeAll} onChange={handleToggleAll} />
              <AgreementLabel>모두 동의</AgreementLabel>
            </AgreementToggle>

            <AgreementList>
              <AgreementItem>
                <AgreementToggle>
                  <CheckOnlyInput
                    checked={agreeChewing}
                    onChange={handleToggleChewing}
                  />
                  <AgreementText>
                    [필수] 개인(신용)정보 수집·이용 동의 (카리페이)
                  </AgreementText>
                </AgreementToggle>
                <ChevronRight
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setShowAgreementSheet(true);
                    setAgreementType("privacy");
                  }}
                />
              </AgreementItem>
              <AgreementItem>
                <AgreementToggle>
                  <CheckOnlyInput
                    checked={agreePartners}
                    onChange={handleTogglePartners}
                  />
                  <AgreementText>
                    [필수] 개인정보 제3자 제공 동의
                    <br />
                    (가맹점, 파트너, 결제중개기관, 금융사)
                  </AgreementText>
                </AgreementToggle>
                <ChevronRight
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setShowAgreementSheet(true);
                    setAgreementType("third-party");
                  }}
                />
              </AgreementItem>
            </AgreementList>
          </ContentBox>

          {/* 통신판매중개자 면책 문구 (§1-5 / 추가 논의사항) — SHOP 전용 */}
          {orderType === "SHOP" && (
            <ShopDisclaimer role="note" aria-label="통신판매중개자 안내">
              {SHOP_DISCLAIMER_TEXT}
            </ShopDisclaimer>
          )}

          {/* 서명 확인 */}
          {signatureData && (
            <SignatureCompleteBox
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <CircleCheckWrapper>
                  <IconCheck color="#007AFF" width="16" height="16" />
                </CircleCheckWrapper>
                <SignatureText>서명 완료되었습니다</SignatureText>
              </div>
              <ChevronRight
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setShowSignatureSheet(true);
                }}
              />
            </SignatureCompleteBox>
          )}
        </ContentWrapper>

        <FooterWrapper>
          <Button
            size="large"
            text={epayBusy && paymentMethod === "EPAY" ? "안전하게 연결하고 있어요" : buttonString}
            width="100%"
            height="48px"
            buttonColor={isButtonActive ? colors.blue600 : colors.gray200}
            textColor={isButtonActive ? colors.white : colors.gray500}
            onClick={() => {
              
              if (!paymentMethod) {
                setAlertMessage("결제 방법을 선택해주세요");
                setShowAlert(true);
                //alert("결제 방법을 선택해주세요");
                return;
              }
              if (paymentMethod === "EPAY") {
                if (!epayPaymentEnabled) {
                  setAlertMessage("NICE 승인·서명 모듈 연결이 완료된 뒤 결제할 수 있어요.");
                  setShowAlert(true);
                  return;
                }
                if (!selectedEpayCardId || !agreeAll) {
                  setAlertMessage("결제할 카드 선택과 필수 약관 동의가 필요해요");
                  setShowAlert(true);
                  return;
                }
              } else if (paymentMethod === "CARD") {
                if (!selectedCard || !agreeAll) {
                  setAlertMessage("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  setShowAlert(true);
                  //alert("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  return;
                }
              } else {
                if (!agreeAll) {
                  setAlertMessage("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  setShowAlert(true);
                  //alert("청구 내역 확인 및 결제 이용약관에 동의해주세요");
                  return;
                }
              }
              // 결제 진행 페이지로 이동
              onPayment();
            }}
          />
        </FooterWrapper>

        {showSignatureSheet && (
          <>
            {/* 배경 dimmed 처리 */}
            <DimmedOverlay />
            <SignatureBottomSheet ref={signatureBottomSheetRef}>
              <SheetHeader>
                <span>서명을 해주세요</span>
                <ResetBtn
                  onClick={() => {
                    signatureRef.current?.clear(); // 서명 데이터 초기화
                    setIsSignatureEmpty(true); // 서명 데이터 비어있는지 확인
                    setSignatureData(null); // 서명 데이터 초기화
                  }}
                >
                  <ResetIcon
                    width="20px"
                    height="20px"
                    color={colors.gray500}
                  />
                  <span>다시하기</span>
                </ResetBtn>
              </SheetHeader>

              <CanvasWrapper ref={canvasContainerRef}>
                <SignatureCanvas
                  ref={signatureRef}
                  backgroundColor="#fff"
                  penColor="#1C1C1E"
                  canvasProps={{ width: canvasWidth, height: canvasHeight }}
                  // 서명 완료 후 서명 데이터 비어있는지 확인
                  onEnd={() => {
                    const empty = signatureRef.current?.isEmpty?.() ?? true;
                    setIsSignatureEmpty(empty);
                  }}
                />
              </CanvasWrapper>

              <Button
                size="large"
                text="완료"
                width="100%"
                height="50px"
                buttonColor={colors.blue600}
                textColor={colors.white}
                onClick={() => {
                  const data = signatureRef.current?.toDataURL();
                  if (data) {
                    setSignatureData({
                      data,
                      width: canvasWidth,
                      height: canvasHeight,
                    });
                    setCustomerSign(data);
                    setShowSignatureSheet(false);
                    // 결제 진행 페이지로 이동
                    //onPayment(data);
                    
                  }
                }}
                disabled={isSignatureEmpty}
              />
            </SignatureBottomSheet>
          </>
        )}

        {!confirmType && (
          <AlertModal
            isOpen={showAlert}
            message={alertMessage}
            onConfirm={() => setShowAlert(false)}
            onClose={() => setShowAlert(false)}
          />
        )}

        {/* {showAgreementModal && (
            <AgreementModal
              onClose={() => setShowAgreementModal(false)}
              type={agreementType}
            />
          )} */}
        {showAgreementSheet && (
          <>
            {/* 배경 dimmed 처리 */}
            <DimmedOverlay />
            <AgreementBottomSheet ref={agreementBottomSheetRef}>
              <SheetHeader>
                <span></span>
                <IconX
                  color="#333"
                  width="24px"
                  height="24px"
                  onClick={() => setShowAgreementSheet(false)}
                  style={{ cursor: "pointer" }}
                />
              </SheetHeader>
              <Content>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content}
                </ReactMarkdown>
              </Content>
            </AgreementBottomSheet>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default BillConfirmPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
  padding: 24px 20px;
  box-sizing: border-box;
  margin: 0 auto;
  overflow-y: auto;
`;

// 페이지 전체 > 래퍼
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

// 페이지 전체 > 헤더 래퍼
const HeaderWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

// 페이지 전체 > 컨텐츠 래퍼
const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 90px;
`;

// 컨텐츠 박스
const ContentBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  padding: 20px 16px;
  border-radius: 12px;
`;

// 컨텐츠 박스 > 서명 완료
const SignatureCompleteBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  padding: 20px 16px;
  border-radius: 12px;

  animation: slideFadeIn 0.4s cubic-bezier(0.5, 1.4, 0.5, 1);
  transform-origin: top;

  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// 컨텐츠 박스 > 청구 내역
const Title = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: ${fonts.body2SemiBold.fontSize};
  color: ${colors.gray900};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 28px;

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Label = styled.div`
  font-size: 14px;
  color: ${colors.gray700};
  white-space: nowrap;
  flex: 0.4;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${colors.gray900};
  text-align: right;
  line-height: 1.6;
  flex: 0.6;
`;

const Divider = styled.div`
  border-top: 1px solid ${colors.gray100};
  margin: 20px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.div`
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
  color: ${colors.gray700};
`;

const TotalValue = styled.div`
  font-family: ${fonts.body1SemiBold.fontFamily};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-size: ${fonts.body1SemiBold.fontSize};
  color: ${colors.gray900};
`;

// 컨텐츠 박스 > 결제 방법
const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 32px;
`;

const PaymentMethodItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.$isActive ? "1.4px solid" : "1px solid")};
  border-color: ${(props) =>
    props.$isActive ? colors.blue600 : colors.gray100};
  background-color: ${(props) =>
    props.$isActive ? colors.blue50 : "transparent"};
  height: 72px;
  border-radius: 8px;
  cursor: pointer;
`;

const PaymentMethodItemTitle = styled.div`
  font-family: ${fonts.body3SemiBold.fontFamily};
  line-height: ${fonts.body3SemiBold.lineHeight};
  font-size: ${fonts.body3SemiBold.fontSize};
  color: ${colors.gray900};
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;

  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: ${fonts.body3Regular.fontSize};

  animation: slideFadeIn 0.4s cubic-bezier(0.5, 1.4, 0.5, 1);
  transform-origin: top;

  @keyframes slideFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const NativeSelect = styled.select<{ $selected?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 12px 14px;
  padding-right: 36px;
  color: ${colors.gray900};
  background-color: ${({ $selected }) =>
    $selected ? colors.gray50 : colors.white};
  border: ${({ $selected }) => ($selected ? "none" : "1px solid")}
    ${colors.gray100};
  border-radius: 8px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: ${({ $selected }) => {
    const stroke = $selected ? colors.gray800 : colors.gray400;
    const encodedStroke = stroke.replace("#", "%23");
    return `url("data:image/svg+xml;utf8,<svg width='24px' height='24px' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M6 8l4 4 4-4' stroke='${encodedStroke}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`;
  }};

  background-repeat: no-repeat;
  background-position: right 14px center;
  background-position-y: 60%;
  background-size: 24px 24px;

  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: ${fonts.body3Regular.fontSize};

  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
`;

// 약관 동의
const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// 체크박스와 문구를 한 label 로 묶어 문구를 눌러도 체크된다.
const AgreementToggle = styled.label`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const Content = styled.article`
  max-width: 1100px;
  line-height: 1.8;
  font-size: 14px;
  font-family: "Noto Sans KR", sans-serif;
  color: #333;
  background: #fff;
  border-radius: 8px;
  padding: 28px 20px;
  height: 100%;
  overflow-y: auto;

  ${({ theme }) => theme.device.mobile} {
    padding: 0;
  }

  h1 {
    font-size: 34px;
    font-weight: 700;
    margin-bottom: 40px;
    color: #1a1a1a;
    border-bottom: 2px solid #eee;
    padding-bottom: 16px;
  }

  h2 {
    font-size: 22px;
    font-weight: 700;
    margin-top: 56px;
    margin-bottom: 28px;
    color: #1a1a1a;
    border-bottom: 1px solid #eee;
    padding-bottom: 12px;
  }

  h3 {
    font-size: 14px;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
    color: #1a1a1a;
  }

  p {
    margin-bottom: 16px;
    color: #4a4a4a;
  }

  ul,
  ol {
    padding-left: 24px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 8px;
    color: #4a4a4a;
  }

  strong {
    font-weight: 600;
    color: #1a1a1a;
  }

  a {
    color: #0066cc;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// 약관 동의 > 체크박스
const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  background-color: white;
  border: 1px solid ${colors.gray100};
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 46%;
    left: 46%;
    width: 16px;
    height: 16px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:checked {
    border-color: ${colors.blue600};
    background-color: ${colors.blue600};
  }

  &:checked::after {
    opacity: 1;
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 11L10 16L17 7' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }
`;

const CheckOnlyInput = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  display: block;
  line-height: 0;

  &::after {
    content: "";
    display: block;
    width: 24px;
    height: 24px;
    background-repeat: no-repeat;
    background-size: 20px 20px;
    background-position: center;
    opacity: 1;
    transition: background-image 0.2s;
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 10.5L9 14.5L15 7.5' stroke='%23E5E8EB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }

  &:checked::after {
    background-image: url("data:image/svg+xml;utf8,<svg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M5 10.5L9 14.5L15 7.5' stroke='%23007AFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  }
`;

const AgreementLabel = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: ${fonts.body2SemiBold.fontSize};
  color: ${colors.gray900};
`;

const AgreementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AgreementText = styled.div`
  flex: 1;
  color: ${colors.gray900};
  font-family: ${fonts.body3Regular.fontFamily};
  line-height: ${fonts.body3Regular.lineHeight};
  font-size: 14px;
`;

// 페이지 전체 > 푸터 래퍼
const FooterWrapper = styled.div`
  max-width: 600px;
  background-color: ${colors.white};
  width: 100%;
  position: fixed;
  margin: 0 auto;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 16px;
  box-sizing: border-box;
`;

// 약관동의 바텀시트
const AgreementBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background-color: ${colors.white};
  padding: 28px 20px;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.35s ease-out;
  z-index: 1000;

  height: 85vh;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }

  ${({ theme }) => theme.device.mobile} {
    height: 85vh;
  }
`;

// 서명 바텀시트
const SignatureBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 600px;
  margin: 0 auto;
  background-color: ${colors.white};
  padding: 28px 20px 28px 20px;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.35s ease-out;
  z-index: 1000;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.gray900};

  font-family: ${fonts.body1SemiBold.fontFamily};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-size: ${fonts.body1SemiBold.fontSize};
`;

const ResetBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${colors.gray500};
  cursor: pointer;

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
`;

const CanvasWrapper = styled.div`
  margin: 20px 0 35px 0;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 180px;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const CircleCheckWrapper = styled.div`
  width: 24px;
  height: 24px;
  background-color: #e9f3ff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SignatureText = styled.div`
  font-family: ${fonts.body2SemiBold.fontFamily};
  line-height: ${fonts.body2SemiBold.lineHeight};
  font-size: 16px;
  color: ${colors.gray900};
`;

// 배경 dimmed 처리
const DimmedOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4); // 적당한 어두움
  z-index: 999; // SignatureBottomSheet보다 낮게
`;


const LineItemList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 28px;
  padding-right: 1px;
  width: 100%;
`;

const LineItem = styled.div`
  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
  color: ${colors.gray600};
  margin-right: 1px;
`;

// ============================================================
// 츄잉샵 (SHOP) 전용 UI
// ============================================================

// 배송 안내 캡션 (§1-2)
const ShippingNotice = styled.div`
  margin-top: 12px;
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
  color: ${colors.gray500};
`;

// 상품 테이블
const ShopItemTable = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  border-top: 1px solid ${colors.gray100};
`;

const ShopItemRowHeader = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 48px 1fr 1.6fr;
  gap: 8px;
  padding: 10px 4px;
  border-bottom: 1px solid ${colors.gray100};
  background-color: ${colors.gray50};

  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: ${fonts.detail2Medium.lineHeight};
  font-size: ${fonts.detail2Medium.fontSize};
  color: ${colors.gray600};
`;

const ShopItemRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 48px 1fr 1.6fr;
  gap: 8px;
  padding: 12px 4px;
  align-items: center;
  border-bottom: 1px solid ${colors.gray100};

  font-family: ${fonts.body3Medium.fontFamily};
  line-height: ${fonts.body3Medium.lineHeight};
  font-size: ${fonts.body3Medium.fontSize};
  color: ${colors.gray900};

  &:last-child {
    border-bottom: none;
  }
`;

const ShopColName = styled.div`
  color: ${colors.gray900};
  word-break: keep-all;
`;

const ShopColCover = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ShopColPublisher = styled.div`
  color: ${colors.gray600};
  word-break: keep-all;
`;

const ShopColQty = styled.div`
  color: ${colors.gray900};
  text-align: right;
  word-break: keep-all;
`;

const ShopCoverImg = styled.img`
  width: 40px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  background-color: ${colors.gray100};
`;

const ShopCoverPlaceholder = styled.div`
  width: 40px;
  height: 48px;
  border-radius: 4px;
  background-color: ${colors.gray100};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  line-height: 1;
`;

// 통신판매중개자 면책 문구 박스 (§1-5 / 추가 논의사항)
const ShopDisclaimer = styled.div`
  padding: 14px 16px;
  background-color: transparent;
  color: ${colors.gray500};
  font-family: ${fonts.detail2Medium.fontFamily};
  line-height: 1.6;
  font-size: ${fonts.detail2Medium.fontSize};
  white-space: pre-wrap;
  word-break: keep-all;
`;
